// Ch78 · ALL LEADS, as a WORK LIST — not a spreadsheet.
//
// Rows sit under the stage they are stuck in, ordered by what it costs to keep
// waiting (lib/leadsWork.ts owns that definition). Every row ends in ONE next
// action, so the page can be worked top to bottom without deciding what to do
// first. Two groups are folded shut by default — people already contacted, and
// expired leads — because they are not the work; neither is ever deleted, and
// both are one click away.
//
// "Read" is the machine's two numbers side by side: the scorer's tier (the dot)
// and, for a converted row, how much of them we actually understand (the bar,
// from the living profile). A row whose profile has not been read shows "—";
// it never shows a zero, which would read as "we know nothing about them".

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InfoDot from "./InfoDot";
import { compactMoney } from "../../lib/moveInBoard";
import { TIMELINE_LANES, toneColor } from "../../lib/leadsTimeline";
import { laneIndex } from "../../lib/leadsTimeline";
import type { WorkGroup, WorkRow } from "../../lib/leadsWork";
import { sendability, canClaimThenText, type Sender } from "../../lib/leadsSend";
import { BULK_LABEL, eligibleFor, type BulkAction, type BulkResult } from "../../lib/leadsBulk";

// Ch78 · Grant (2026-09-10): "the bottom section should focus on the table
// view". The Calendar option here was a SECOND calendar drawing a different
// population from the hero above it, with no label saying which was which.
// One calendar, up top, reading the same filter as this table.
// Ch78 · Grant (2026-09-10): "cards view here is kinda useless, can prob
// remove that toggle". It was the same rows with fewer columns and no header,
// so it could not be scanned and could not be sorted — a worse table wearing a
// different name. One list, one shape.

export interface LeadsWorkListProps {
  groups: WorkGroup[];
  /** Total rows before the search box narrowed anything — the workload number. */
  totalRows: number;
  expiredCount: number;
  /** client id -> understanding 0..1, or null when the profile could not be read. */
  understanding: Map<string, number | null>;
  agentNames: Map<string, string>;
  onOpen: (row: WorkRow) => void;
  onAction: (row: WorkRow) => void;
  /** Ch83 · open the composer for this person. Absent = no send affordance. */
  onSend?: (row: WorkRow) => void;
  /**
   * PKT-0666 · who is looking. Required alongside onSend: without it the row
   * cannot tell an allowed send from one the route will 403, which is exactly
   * the defect this replaces.
   */
  sender?: Sender;
  /**
   * PKT-0666 §F1 · claim this unclaimed lead, then open the composer on the
   * client it becomes. Absent = the row falls back to its ordinary action.
   */
  onClaimToText?: (row: WorkRow) => void;
  /** Assigning a lead to somebody else is an admin override server-side. */
  canAssign: boolean;
  assignableAgents: Array<{ id: string; name: string }>;
  onBulk: (action: BulkAction, rows: WorkRow[], ownerId?: string) => Promise<BulkResult>;
  /** Injected for deterministic tests; defaults to the wall clock. */
  now?: Date;
}

const LIST_INFO =
  "A work list, not a spreadsheet: grouped by the stage each person is stuck in and ordered by the cost " +
  "of waiting — how long they have waited, weighted by how soon they must move and how credible the " +
  "scorer finds them, with budget breaking ties. It is a stated heuristic over stated fields, not a " +
  "prediction. \"Read\" is the scorer's tier (dot) and how much of them the living profile understands " +
  "(bar). The last column is the one thing to do next. Searching narrows the rows; the group counts are " +
  "the real workload and never move.";

function tierLabel(row: WorkRow): string {
  return TIMELINE_LANES[laneIndex(row.person.tier)]?.label ?? "Not scored";
}

function moveInText(row: WorkRow, todayMs: number): { text: string; past: boolean } {
  const p = row.person;
  if (p.dateMs === null) return { text: p.moveInText ? `“${p.moveInText}”` : "—", past: false };
  const d = new Date(p.dateMs);
  const text = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(d);
  return { text: p.dateMs < todayMs ? `${text} · past` : text, past: p.dateMs < todayMs };
}

export default function LeadsWorkList({
  groups,
  totalRows,
  expiredCount,
  understanding,
  agentNames,
  onOpen,
  onAction,
  onSend,
  sender,
  onClaimToText,
  canAssign,
  assignableAgents,
  onBulk,
  now,
}: LeadsWorkListProps) {
  const theme = useTheme();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [assignAnchor, setAssignAnchor] = useState<HTMLElement | null>(null);
  const today = useMemo(() => now ?? new Date(), [now]);
  const todayMs = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime(),
    [today],
  );

  const visibleRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  // A row that scrolled out of the filter is no longer selectable work; keeping
  // it selected would let a bulk action touch a row nobody can see.
  const selectedRows = useMemo(
    () => visibleRows.filter((r) => selected.has(r.key)),
    [visibleRows, selected],
  );
  const allVisibleSelected = visibleRows.length > 0 && selectedRows.length === visibleRows.length;

  const toggleRow = (key: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const runBulkAction = async (action: BulkAction, ownerId?: string) => {
    setBusy(true);
    setAssignAnchor(null);
    try {
      const result = await onBulk(action, selectedRows, ownerId);
      setBulkResult(result);
      // Only clear what actually moved; a failed row stays selected so the
      // operator can see it and retry rather than hunting for it again.
      const failedKeys = new Set(result.failures.map((f) => f.key));
      setSelected((s) => new Set([...s].filter((k) => failedKeys.has(k))));
    } finally {
      setBusy(false);
    }
  };

  const isOpen = (g: WorkGroup) => open[g.key] ?? !g.collapsedByDefault;

  const readCell = (row: WorkRow) => {
    const color = toneColor(theme, row.person.tone);
    const u = row.person.client ? understanding.get(row.person.client.id) : undefined;
    const pct = typeof u === "number" ? Math.round(u * 100) : null;
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flex: "none" }} />
        <Typography variant="caption">{tierLabel(row)}</Typography>
        {row.person.kind === "client" && (
          <Tooltip arrow title={pct === null ? "The living profile could not be read for this person" : `The living profile knows ${pct}% of what it asks about`}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 44, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.12), overflow: "hidden" }}>
                {pct !== null && <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: "info.main" }} />}
              </Box>
              <Typography variant="caption" color="text.secondary">{pct === null ? "—" : `${pct}%`}</Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    );
  };

  const nameCell = (row: WorkRow) => (
    <Box
      component="button"
      onClick={() => onOpen(row)}
      data-testid={`work-open-${row.key}`}
      sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, fontSize: 13, color: "text.primary", textAlign: "left" }}
    >
      {row.person.name}
    </Box>
  );

  // Ch83 · when the action IS a text, the row sends it. The Admin card said 10
  // outbound texts in this system's whole history — the pipe was fine, the loop
  // was seven steps ending in a button that only recorded the past. A next
  // action that performs itself is the difference.
  //
  // The send affordance is drawn ONLY when the server would accept it:
  // sendability() mirrors the routes' real predicates (a phone, and for a lead
  // the 10DLC verify gate), so an operator never presses Send and gets a 400.
  // Refusals still show the verb, disabled, with the reason — hiding the row's
  // action entirely would leave them wondering what to do instead.
  const actionCell = (row: WorkRow) => {
    // "Follow up" is a text too. Ch83: it was left out of the first cut, and
    // the consequence only surfaced once templates existed — a contacted_waiting
    // row says "Follow up", so it never opened the composer, so every follow-up
    // template was unreachable from the work list. A "Call ·" action stays out
    // on purpose: it names a different act, and for someone with no text
    // consent it is the ONLY act.
    const textish = /^(Send text|Text|Follow up)/.test(row.nextAction);
    const can = textish && onSend && sender ? sendability(row, sender) : null;
    // §F1 · an unclaimed lead cannot be texted by anyone under "assigned only",
    // so the row offers the step that MAKES it textable. This is checked before
    // `textish`, because an unclaimed row's action is "Claim" — not a text verb
    // — so the send branches never see it.
    if (onClaimToText && sender && canClaimThenText(row, sender)) {
      return (
        <Tooltip arrow title="Claims this lead to you and opens the message — we all share one number, so the assigned locator sends it">
          <Box
            component="button"
            onClick={() => onClaimToText(row)}
            data-testid={`work-claim-to-text-${row.key}`}
            sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, fontSize: 12.5, color: "primary.main", textAlign: "left", whiteSpace: "nowrap" }}
          >
            Claim to text →
          </Box>
        </Tooltip>
      );
    }
    if (can && can.canSend) {
      return (
        <Box
          component="button"
          onClick={() => onSend!(row)}
          data-testid={`work-send-${row.key}`}
          sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, fontSize: 12.5, color: "primary.main", textAlign: "left", whiteSpace: "nowrap" }}
        >
          {row.nextAction} →
        </Box>
      );
    }
    if (can && !can.canSend) {
      return (
        <Tooltip arrow title={`${can.reason}. ${can.remedy}.`}>
          <Box
            component="button"
            onClick={() => onAction(row)}
            data-testid={`work-send-blocked-${row.key}`}
            sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12.5, color: "text.disabled", textAlign: "left", whiteSpace: "nowrap" }}
          >
            {row.nextAction} · {can.reason}
          </Box>
        </Tooltip>
      );
    }
    return (
      <Box
        component="button"
        onClick={() => onAction(row)}
        data-testid={`work-action-${row.key}`}
        sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, fontSize: 12.5, color: "primary.main", textAlign: "left", whiteSpace: "nowrap" }}
      >
        {row.nextAction}
      </Box>
    );
  };

  const waitingCell = (row: WorkRow) => (
    <Typography
      variant="caption"
      sx={{ fontWeight: row.overdue ? 800 : 600, color: row.overdue ? "error.main" : "text.secondary" }}
    >
      {row.waitingLabel}
    </Typography>
  );

  const locator = (row: WorkRow) =>
    (row.ownerId ? agentNames.get(row.ownerId) : null) ?? row.person.agentName ?? "—";

  const groupHeader = (g: WorkGroup) => (
    <Box
      component="button"
      onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen(g) }))}
      data-testid={`work-group-${g.key}`}
      sx={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        bgcolor: alpha(theme.palette.text.primary, 0.05),
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "text.secondary" }}>
        {g.label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 800 }}>{g.rows.length}</Typography>
      <Box sx={{ flex: 1 }} />
      {g.note && (
        <Typography variant="caption" sx={{ fontWeight: 700, color: g.key === "unclaimed" ? "error.main" : "primary.main" }}>
          {g.note}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled">{isOpen(g) ? "hide" : "show"}</Typography>
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} data-testid="leads-work-list">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={800}>All leads</Typography>
        <InfoDot text={LIST_INFO} testId="work-info" />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" data-testid="work-counts">
          {totalRows} live · {expiredCount} expired
        </Typography>
      </Stack>


      {/* The bulk bar names what each verb can actually touch. A button that
          reads "Claim" over a selection containing two clients would do nothing
          to them and say nothing about it. */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Checkbox
          size="small"
          checked={allVisibleSelected}
          indeterminate={selectedRows.length > 0 && !allVisibleSelected}
          onChange={() =>
            setSelected(allVisibleSelected ? new Set() : new Set(visibleRows.map((r) => r.key)))
          }
          inputProps={{ "aria-label": "Select every visible row", "data-testid": "work-select-all" } as never}
        />
        <Typography variant="caption" color="text.secondary" data-testid="work-selected-count">
          {selectedRows.length} selected
        </Typography>
        {/* Restore sits beside Archive because it is the way BACK from it.
            Both are disabled unless the selection actually contains rows they
            can touch, so the bar never offers a verb that would do nothing. */}
        {(["claim", "assign", "archive", "restore"] as const).map((action) => {
          const eligible = eligibleFor(selectedRows, action).length;
          const blocked = action === "assign" && !canAssign;
          return (
            <Tooltip
              key={action}
              arrow
              title={
                blocked
                  ? "Assigning a lead to somebody else is an admin override; you can only claim to yourself."
                  : eligible === 0
                    ? `Nothing in this selection can be ${action === "archive" ? "archived" : "claimed"}.`
                    : `${eligible} of ${selectedRows.length} selected`
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy || blocked || eligible === 0}
                  data-testid={`work-bulk-${action}`}
                  onClick={(e) =>
                    action === "assign" ? setAssignAnchor(e.currentTarget) : void runBulkAction(action)
                  }
                >
                  {BULK_LABEL[action]}{eligible > 0 ? ` (${eligible})` : ""}
                </Button>
              </span>
            </Tooltip>
          );
        })}
        <Menu anchorEl={assignAnchor} open={Boolean(assignAnchor)} onClose={() => setAssignAnchor(null)}>
          {assignableAgents.length === 0 ? (
            <MenuItem disabled>No locators to assign to</MenuItem>
          ) : (
            assignableAgents.map((a) => (
              <MenuItem key={a.id} data-testid={`work-assign-${a.id}`} onClick={() => void runBulkAction("assign", a.id)}>
                {a.name}
              </MenuItem>
            ))
          )}
        </Menu>
      </Stack>

      {bulkResult && (
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 1.5, color: bulkResult.failures.length ? "warning.main" : "success.main" }}
          data-testid="work-bulk-result"
        >
          {bulkResult.summary}
        </Typography>
      )}

      {groups.length === 0 ? (
        <Box sx={{ p: 5, textAlign: "center" }} data-testid="work-empty">
          <Typography variant="body2" color="text.secondary">
            Nobody matches. Clear the search or the filters above.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="work-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Name</TableCell>
                <TableCell>Waiting</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell>Move-in</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>Locator</TableCell>
                <TableCell>Next action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>{groupHeader(g)}</TableCell>
                  </TableRow>
                  {isOpen(g) &&
                    g.rows.map((row) => {
                      const mi = moveInText(row, todayMs);
                      return (
                        <TableRow key={row.key} hover data-testid={`work-row-${row.key}`} selected={selected.has(row.key)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selected.has(row.key)}
                              onChange={() => toggleRow(row.key)}
                              inputProps={{ "aria-label": `Select ${row.person.name}`, "data-testid": `work-select-${row.key}` } as never}
                            />
                          </TableCell>
                          <TableCell>{nameCell(row)}</TableCell>
                          <TableCell>{waitingCell(row)}</TableCell>
                          <TableCell><Typography variant="caption">{row.sourceLabel ?? "—"}</Typography></TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {row.person.budget === null ? "—" : compactMoney(row.person.budget)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: mi.past ? "warning.main" : "text.secondary" }}>{mi.text}</Typography>
                          </TableCell>
                          <TableCell>{readCell(row)}</TableCell>
                          <TableCell><Typography variant="caption">{locator(row)}</Typography></TableCell>
                          <TableCell>{actionCell(row)}</TableCell>
                        </TableRow>
                      );
                    })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
